import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlassCard } from '../components/ui/GlassCard';
import { ArrowLeft } from 'lucide-react';

export const Legal = () => {
  const { t } = useTranslation();
  const { type } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  const email = 'support@getinfinityblue.com';

  const content: Record<string, { title: string; body: React.ReactNode }> = {
    terms: {
      title: t('legal.terms.title'),
      body: (
        <>
          <p>{t('legal.terms.effectiveDate')}</p>

          <h3>{t('legal.terms.s1Title')}</h3>
          <p>{t('legal.terms.s1Body')}</p>

          <h3>{t('legal.terms.s2Title')}</h3>
          <p>{t('legal.terms.s2Body')}</p>

          <h3>{t('legal.terms.s3Title')}</h3>
          <p>{t('legal.terms.s3Body')}</p>

          <h3>{t('legal.terms.s4Title')}</h3>
          <p>{t('legal.terms.s4_1')}</p>
          <p>{t('legal.terms.s4_2')}</p>
          <p>{t('legal.terms.s4_3')}</p>

          <h3>{t('legal.terms.s5Title')}</h3>
          <p>{t('legal.terms.s5_1')}</p>
          <p>{t('legal.terms.s5_2')}</p>
          <p>{t('legal.terms.s5_3')}</p>

          <h3>{t('legal.terms.s6Title')}</h3>
          <p>{t('legal.terms.s6Body')}</p>

          <h3>{t('legal.terms.s7Title')}</h3>
          <p>{t('legal.terms.s7Body')}</p>

          <h3>{t('legal.terms.s8Title')}</h3>
          <p>
            {t('legal.terms.s8Body')}{' '}
            <a href={`mailto:${email}`} className="text-glass-cyan hover:underline">{email}</a>
          </p>
        </>
      ),
    },
    privacy: {
      title: t('legal.privacy.title'),
      body: (
        <>
          <p>{t('legal.privacy.effectiveDate')}</p>

          <h3>{t('legal.privacy.s1Title')}</h3>
          <p>{t('legal.privacy.s1Body')}</p>

          <h3>{t('legal.privacy.s2Title')}</h3>
          <p>{t('legal.privacy.s2_1')}</p>
          <p>
            {t('legal.privacy.s2_2')}{' '}
            <strong>{t('legal.privacy.s2_2Note')}</strong>
          </p>

          <h3>{t('legal.privacy.s3Title')}</h3>
          <p>{t('legal.privacy.s3Intro')}</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
            <li>{t('legal.privacy.s3Item1')}</li>
            <li>{t('legal.privacy.s3Item2')}</li>
            <li>{t('legal.privacy.s3Item3')}</li>
            <li>{t('legal.privacy.s3Item4')}</li>
          </ul>

          <h3>{t('legal.privacy.s4Title')}</h3>
          <p>{t('legal.privacy.s4Body')}</p>

          <h3>{t('legal.privacy.s5Title')}</h3>
          <p>{t('legal.privacy.s5Intro')}</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
            <li><strong>{t('legal.privacy.s5Item1')}</strong></li>
            <li><strong>{t('legal.privacy.s5Item2')}</strong></li>
          </ul>

          <h3>{t('legal.privacy.s6Title')}</h3>
          <p>{t('legal.privacy.s6Body')}</p>
        </>
      ),
    },
    aup: {
      title: t('legal.aup.title'),
      body: (
        <>
          <p>{t('legal.aup.effectiveDate')}</p>
          <p>{t('legal.aup.intro')}</p>

          <h3>{t('legal.aup.s1Title')}</h3>
          <p>{t('legal.aup.s1Intro')}</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
            <li>{t('legal.aup.s1Item1')}</li>
            <li>{t('legal.aup.s1Item2')}</li>
            <li>{t('legal.aup.s1Item3')}</li>
            <li>{t('legal.aup.s1Item4')}</li>
            <li>{t('legal.aup.s1Item5')}</li>
          </ul>

          <h3>{t('legal.aup.s2Title')}</h3>
          <p>{t('legal.aup.s2Intro')}</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
            <li>{t('legal.aup.s2Item1')}</li>
            <li>{t('legal.aup.s2Item2')}</li>
            <li>{t('legal.aup.s2Item3')}</li>
          </ul>

          <h3>{t('legal.aup.s3Title')}</h3>
          <p>{t('legal.aup.s3Body')}</p>
        </>
      ),
    },
    refund: {
      title: t('legal.refund.title'),
      body: (
        <>
          <p>{t('legal.refund.effectiveDate')}</p>

          <h3>{t('legal.refund.s1Title')}</h3>
          <p>{t('legal.refund.s1Body')}</p>

          <h3>{t('legal.refund.s2Title')}</h3>
          <p>{t('legal.refund.s2_1')}</p>
          <p>{t('legal.refund.s2_2')}</p>

          <h3>{t('legal.refund.s3Title')}</h3>
          <p>
            {t('legal.refund.s3Body')}{' '}
            <a href={`mailto:${email}`} className="text-glass-cyan hover:underline">{email}</a>{' '}
            {t('legal.refund.s3BodySuffix')}
          </p>
        </>
      ),
    },
  };

  const currentContent = content[type || 'terms'] || content.terms;

  return (
    <div className="container mx-auto px-6 py-20">
      <div className="flex flex-col md:flex-row gap-12 max-w-6xl mx-auto">
        {/* Sidebar Navigation */}
        <div className="md:w-64 shrink-0 space-y-2">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('legal.backToHome')}
          </Link>

          <h3 className="font-display font-bold text-white mb-4 px-4">{t('legal.legalCenter')}</h3>
          <nav className="flex flex-col space-y-1">
            {Object.entries(content).map(([key, data]) => (
              <Link
                key={key}
                to={`/legal/${key}`}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  (type === key || (!type && key === 'terms'))
                    ? 'bg-glass-cyan/10 text-glass-cyan border border-glass-cyan/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {data.title}
              </Link>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <GlassCard className="flex-grow p-12">
          <h1 className="text-4xl font-display font-bold mb-8">{currentContent.title}</h1>
          <div className="prose prose-invert prose-lg max-w-none text-gray-400
            prose-headings:font-display prose-headings:text-white prose-headings:font-bold
            prose-a:text-glass-cyan prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white">
            {currentContent.body}

            <div className="mt-16 pt-8 border-t border-glass-border text-sm text-gray-500">
              <p className="font-bold text-white mb-1">{t('legal.companyName')}</p>
              <p>{t('legal.companyAddress')}</p>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};
